import { call, takeLatest, all } from 'redux-saga/effects';
import * as api from '@api/externalUserSubjects';
import { actions } from './ExternalUserSubjects.slice';
import {
  ExternalUserSubjectsListFetchAction,
  ExternalUserSubjectDetailsFetchAction,
  ExternalUserTrialsFetchAction,
  ExternalUserContextFetchAction,
  ClinicalTrialContext,
} from './ExternalUserSubjects.types';
import { Resp, dispatch } from '../helpers';
import { runToast } from '../tools';
import { SiteSubjectDetails } from '@dynamika-shared/types/subjects';
import { ClinicalTrialType } from '@dynamika-shared/types/clinicalTrials';

export function* fetchExternalUserSubjectsListWorker(
  action: ExternalUserSubjectsListFetchAction
) {
  try {
    const { clinicalTrialId, args } = action.payload;
    const response: Resp<typeof api.fetchExternalUserSubjects> = yield call<
      typeof api.fetchExternalUserSubjects
    >(api.fetchExternalUserSubjects, clinicalTrialId, args);
    yield dispatch(actions.fetchExternalUserSubjectsListSuccess(response));
  } catch (error) {
    yield dispatch(actions.fetchExternalUserSubjectsListFailure(error as Error));
  }
}

export function* fetchExternalUserSubjectDetailsWorker(
  action: ExternalUserSubjectDetailsFetchAction
) {
  const { clinicalTrialId, patientId } = action.payload;
  try {
    // Call both endpoints in parallel
    const [patientDetails, visitsResponse]: [
      Resp<typeof api.fetchExternalUserSubjectDetails>,
      Resp<typeof api.fetchExternalUserPatientVisits>
    ] = yield all([
      call(api.fetchExternalUserSubjectDetails, clinicalTrialId, patientId),
      call(api.fetchExternalUserPatientVisits, clinicalTrialId, patientId),
    ]);

    // Map new response to SiteSubjectDetails format for backward compatibility
    const combinedResponse = {
      id: patientDetails.id,
      dob: patientDetails.dob,
      siteId: visitsResponse.siteId,
      cohortId: patientDetails.cohortName,
      patientCode: patientDetails.code,
      siteName: patientDetails.siteName,
      status: patientDetails.status,
      visits: visitsResponse.visits.map(visit => ({
        id: visit.id,
        visitName: visit.visitName,
        visitDefinitionId: visit.visitDefinitionId,
        status: visit.visitStatus,
        expectedDate: visit.forecastDate,
        latestUpload: visit.uploadDate,
        type: '',
        assetAcquisitionDate: visit.assetAcquisitionDate,
        visitOrder: visit.visitOrder,
        visitDate: visit.visitDate,
        earlyTerminated: false,
      })),
    };

    yield dispatch(actions.fetchExternalUserSubjectDetailsSuccess(combinedResponse as Partial<SiteSubjectDetails> | undefined));
  } catch (error) {
    yield dispatch(actions.fetchExternalUserSubjectDetailsFailure(error as Error));
  }
}

export function* fetchTrialsWorker(action: ExternalUserTrialsFetchAction) {
  try {
    const { role } = action.payload || {};
    const response: Resp<typeof api.fetchExternalUserTrials> = yield call(
      api.fetchExternalUserTrials,
      role
    );
    // Response is PaginatedResponse, extract items
    const trials = (response as { items?: ClinicalTrialType[] })?.items || [];
    yield dispatch(actions.fetchTrialsSuccess(trials));
  } catch (error) {
    yield dispatch(actions.fetchTrialsFailure(error as Error));
    yield call(runToast, 'error', 'Failed to fetch trials');
  }
}

export function* fetchContextWorker(action: ExternalUserContextFetchAction): Generator {
  try {
    const { clinicalTrialId } = action.payload;
    const response = (yield call(
      api.fetchClinicalTrialContext,
      clinicalTrialId
    )) as ClinicalTrialContext;
    yield dispatch(actions.fetchContextSuccess(response));
  } catch (error) {
    yield dispatch(actions.fetchContextFailure(error as Error));
    yield call(runToast, 'error', 'Failed to fetch clinical trial context');
  }
}

export function* ExternalUserSubjectsSaga() {
  yield takeLatest(
    actions.fetchExternalUserSubjectsList,
    fetchExternalUserSubjectsListWorker
  );
  yield takeLatest(
    actions.fetchExternalUserSubjectDetails,
    fetchExternalUserSubjectDetailsWorker
  );
  yield takeLatest(actions.fetchTrials, fetchTrialsWorker);
  yield takeLatest(actions.fetchContext, fetchContextWorker);
}
